import { apiError } from "../../../../../../utils/formErrors";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import React, { useState, useEffect } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import EmptyState from "../../../../../../component/EmptyState";
import { headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getRates, createRate, deleteRate, getProjects } from "../../../../../../services/timesheet";
import { useOrgEmployees } from "../../../../../../services/useOrgData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { formatRate, currencyForProject, currencySymbol } from "../../../../../../utils/currency";
import { tokens, displayFont } from "../../../../../../theme";

const nameOf = (e) => (e ? `${e.fname || ""} ${e.lname || ""}`.trim() : "");

const EmployeeRate = () => {
  const notify = useNotify();
  const { data: employees } = useOrgEmployees();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee: "", project: "", billRatePerHour: "", costRatePerHour: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true); setError(false);
    try {
      const [r, p] = await Promise.all([getRates(), getProjects().catch(() => null)]);
      if (r?.success === false) throw new Error(r?.message || "Failed to load rates");
      setRows(Array.isArray(r?.data) ? r.data : []);
      setProjects(Array.isArray(p?.data) ? p.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load rates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!form.employee && !form.project) { notify.error("Pick an employee and/or project"); return; }
    setSaving(true);
    try {
      const res = await createRate({
        employee: form.employee || undefined, project: form.project || undefined,
        billRatePerHour: Number(form.billRatePerHour) || 0, costRatePerHour: Number(form.costRatePerHour) || 0,
      });
      if (res?.success === false) throw apiError(res, "Could not create rate");
      notify.success("Rate added");
      setOpen(false); setForm({ employee: "", project: "", billRatePerHour: "", costRatePerHour: "" });
      fetchData();
    } catch (e) { notify.error(e?.message || "Could not create rate"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteRate(id); notify.success("Rate removed"); fetchData(); }
    catch (e) { notify.error(e?.message || "Could not remove rate"); }
  };

  const ch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Box sx={{ width: "100%", padding: "1.5rem 1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Billing Rates</Box>
        <BtnPrimary onClick={() => setOpen(true)} sx={{ borderRadius: "8px" }}>Add Rate</BtnPrimary>
      </Stack>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem" }}>
        Most specific match wins: employee + project &gt; project &gt; employee.
      </Box>

      {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Employee</TableCell>
              <TableCell sx={headCellSx}>Project</TableCell>
              <TableCell sx={headCellSx} align="right">Bill /hr</TableCell>
              <TableCell sx={headCellSx} align="right">Cost /hr</TableCell>
              <TableCell sx={{ ...headCellSx, width: "4rem" }}></TableCell>
            </TableRow>
          </TableHead>
            <TableBody><TableSkeleton columns={5} /></TableBody>
          </Table>
        ) : error ? (
        <EmptyState variant="error" title="Couldn't load rates" onRetry={fetchData} />
      ) : rows.length === 0 ? (
        <EmptyState title="No rates yet" description="Add billing/cost rates so timesheet hours become billable amounts." />
      ) : (
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Employee</TableCell>
              <TableCell sx={headCellSx}>Project</TableCell>
              <TableCell sx={headCellSx} align="right">Bill /hr</TableCell>
              <TableCell sx={headCellSx} align="right">Cost /hr</TableCell>
              <TableCell sx={{ ...headCellSx, width: "4rem" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r._id}>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.employee ? nameOf(r.employee) : "— Any —"}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{r.project?.name || "— Any —"}</TableCell>
                {/* A bare number is not a rate. A rate on a project follows
                    that project's client currency, so two rows can legitimately
                    be in different money. */}
                <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }} align="right">
                  {formatRate(r.billRatePerHour, currencyForProject(r.project, projects))}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted, fontVariantNumeric: "tabular-nums" }} align="right">
                  {formatRate(r.costRatePerHour, currencyForProject(r.project, projects))}
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <IconButton aria-label="Delete" size="small" onClick={() => remove(r._id)} sx={{ color: tokens.textMuted, "&:hover": { color: tokens.danger } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "28rem", maxWidth: "94vw" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, mb: "1.25rem" }}>Add Rate</Box>
        <FormField label="Employee (optional)" type="select" options={[{ name: "— Any —", value: "" }, ...(employees || []).map((e) => ({ name: nameOf(e), value: e._id }))]} value={form.employee} onChange={(e) => ch("employee", e.target.value)} fullwidth />
        <FormField label="Project (optional)" type="select" options={[{ name: "— Any —", value: "" }, ...projects.map((p) => ({ name: p.name, value: p._id }))]} value={form.project} onChange={(e) => ch("project", e.target.value)} fullwidth />
        <FormField label={`Bill rate / hour (${currencySymbol(currencyForProject(form.project, projects))})`} type="number" value={form.billRatePerHour} onChange={(e) => ch("billRatePerHour", e.target.value)} fullwidth />
        <FormField label={`Cost rate / hour (${currencySymbol(currencyForProject(form.project, projects))})`} type="number" value={form.costRatePerHour} onChange={(e) => ch("costRatePerHour", e.target.value)} fullwidth />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1.5rem" }}>
          <BtnSecondary onClick={() => setOpen(false)} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? "Adding…" : "Add"}</BtnPrimary>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default EmployeeRate;
